import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy } from '@angular/core';

import { TrainingOverviewRowTap } from 'components/cs-training-overview/cs-training-overview';
import { LL } from 'i18n/i18n.service';
import { ExerciseConfigurationPageResult } from 'pages/exercise-configuration/exercise-configuration-interface';
import { CsPage } from 'providers/nav-controller';
import { isCoachingDashboardOnAsync, watchCoachingDashboard } from 'services/coaching-dashboard-flag';
import { ConfigDbService } from 'services/config-db.service';
import { isDatabaseRecovering } from 'services/database-unavailable-error';
import { DocWatcher } from 'services/database-wrapper';
import { LoggingService } from 'services/logging.service';
import {
  TRAINING_OVERVIEW_SESSIONS_STEP, TRAINING_OVERVIEW_WINDOW_DAYS, TrainingOverviewModel, showEarlierStep,
} from 'services/training-overview';
import { ExerciseSettingsContext } from 'services/training-overview-actions';
import { TrainingOverviewActionsService } from 'services/training-overview-actions.service';
import { TrainingOverviewDataService } from 'services/training-overview-data.service';
import { UserDb } from 'services/user-db.service';
import { TrainingOverviewPageParams } from './training-overview-interface';

/** the key the shown-session counts are kept under for a section: its workout's index, or this for "Other sessions" */
const OTHER_SESSIONS_KEY = 'other';

/**
 * The Training Overview (#5456, phase 3): the signed-in account's logged training as one table
 * per workout of the program. A coach sees a client's by entering the client's account.
 *
 * Behind the coaching dashboard switch: with it off the page draws one sentence and loads
 * nothing. The page only hosts: the rules are `services/training-overview.ts`, the loading is
 * `TrainingOverviewDataService`, the drawing is `cs-training-overview` and the exercise menu is
 * `TrainingOverviewActionsService`. See design-docs/coaching-dashboard.md, "Phase 3: the Training
 * Overview".
 */
@Component({
  selector: 'page-training-overview',
  templateUrl: 'training-overview.html',
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class TrainingOverviewPage extends CsPage<TrainingOverviewPageParams> implements OnDestroy {
  protected LL = LL;

  protected coachingDashboardOn = false;
  protected model?: TrainingOverviewModel;
  protected busy = false;
  protected failed = false;
  /** per section of the model, whether "Show 10 earlier" has anything to show */
  protected canShowEarlier: boolean[] = [];
  protected olderLogsDownloading = false;

  /** how many days back the logs are loaded; grows when "Show 10 earlier" runs out of loaded sessions */
  private windowDays = TRAINING_OVERVIEW_WINDOW_DAYS;
  /** true when a finished workout older than the loaded window is on this device */
  private mayHaveOlder = false;
  /** how many sessions each section shows, by `OTHER_SESSIONS_KEY` or the workout's index; absent means the first step */
  private shownBySection = new Map<number | string, number>();
  /** counts loads, so an answer that arrives after a newer request is dropped */
  private loadCounter = 0;
  private docWatcher: DocWatcher;
  private unwatchCoachingDashboard?: () => void;

  constructor(
    private cd: ChangeDetectorRef,
    private userDb: UserDb,
    private configDb: ConfigDbService,
    private data: TrainingOverviewDataService,
    private actions: TrainingOverviewActionsService,
    private logging: LoggingService,
  ) {
    super();
  }

  protected get loadedWeeks(): number {
    return Math.round(this.windowDays / 7);
  }

  ngOnDestroy() {
    this.docWatcher?.destroy();
    this.unwatchCoachingDashboard?.();
  }

  /** Runs when the page is opened and again each time a page pushed from it closes. */
  async ionViewCanEnter() {
    if (!this.docWatcher) {
      // a set logged on another device, or an edit made on a pushed page, redraws the grid. The
      // callback returns the reload, so the watcher waits for it before taking the next change
      this.docWatcher = this.userDb.database.createDocWatcher(_changes => {
        if (this.viewDestroyed) return undefined;
        return this.reloadAsync();
      });
    }
    this.coachingDashboardOn = await isCoachingDashboardOnAsync(this.configDb);
    if (!this.unwatchCoachingDashboard) {
      this.unwatchCoachingDashboard = watchCoachingDashboard(this.configDb, isOn => this.onCoachingDashboardSwitched(isOn));
    }
    // not awaited: the page opens at once and shows its loading state
    void this.reloadAsync();
  }

  /** the switch was flipped while the page is open */
  private onCoachingDashboardSwitched(isOn: boolean) {
    if (this.viewDestroyed || isOn === this.coachingDashboardOn) return;
    this.coachingDashboardOn = isOn;
    if (!isOn) {
      this.model = undefined;
    }
    this.cd.markForCheck();
    void this.reloadAsync();
  }

  protected onRetry() {
    void this.reloadAsync();
  }

  protected onRowTap(tap: TrainingOverviewRowTap) {
    void this.actions.openRowMenuAsync(tap.row, tap.event, 'onConfigurationResult', () => void this.reloadAsync());
  }

  /** The Exercise Settings window, opened from the menu, closed. Named by `returnCallback` in `onRowTap`. */
  async onConfigurationResult(context: ExerciseSettingsContext, result: ExerciseConfigurationPageResult) {
    await this.actions.handleConfigurationResultAsync(context, result);
    if (this.viewDestroyed) return;
    await this.reloadAsync();
  }

  /**
   * "Show earlier" for one section; what a press changes is decided by `showEarlierStep`. A press
   * while a load is running is ignored: it would be worked out from the grid as it was before that
   * load, and could widen the window a second time for one wish.
   */
  protected onShowEarlier(sectionIndex: number) {
    const section = this.model?.sections[sectionIndex];
    if (!section || this.busy) return;
    const step = showEarlierStep(section, this.mayHaveOlder, this.windowDays);
    this.windowDays = step.windowDays;
    this.shownBySection.set(section.planReference ?? OTHER_SESSIONS_KEY, step.shownCount);
    void this.reloadAsync();
  }

  /** how many sessions a section shows: what "Show earlier" raised it to, or the first step */
  private shownCount(planReference: number | undefined): number {
    return this.shownBySection.get(planReference ?? OTHER_SESSIONS_KEY) ?? TRAINING_OVERVIEW_SESSIONS_STEP;
  }

  /** Loads the model again. Loads can overlap (the watcher fires while a press is loading): the newest wins. */
  private async reloadAsync(): Promise<void> {
    if (!this.coachingDashboardOn || this.viewDestroyed) return;
    const loadNumber = ++this.loadCounter;
    this.busy = true;
    this.cd.markForCheck();
    try {
      const result = await this.data.loadAsync(this.docWatcher, this.windowDays, planReference => this.shownCount(planReference));
      // if a newer load started, or the page is gone, this answer is stale
      if (this.viewDestroyed || loadNumber !== this.loadCounter) return;
      this.model = result.model;
      this.mayHaveOlder = result.mayHaveOlder;
      this.olderLogsDownloading = result.olderLogsDownloading;
      this.canShowEarlier = result.model.sections.map(section => section.sessionCount > section.columns.length || result.mayHaveOlder);
      this.failed = false;
    } catch (err) {
      if (this.viewDestroyed || loadNumber !== this.loadCounter) return;
      // the grid is replaced by "could not load" with a Retry button either way
      this.failed = true;
      if (isDatabaseRecovering(err)) {
        this.logging.trackSilentProblem('trainingOverviewInterruptedByDatabaseRecovery', { database: err.databaseIdentifier });
      } else {
        this.logging.trackAbnormalException(err);
      }
    } finally {
      if (!this.viewDestroyed && loadNumber === this.loadCounter) {
        this.busy = false;
        this.cd.markForCheck();
      }
    }
  }
}
